import React, { useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueueStore } from '../../stores/queueStore';
import { useDashboardStore } from '../../stores/dashboardStore';
import { useTicketStore } from '../../stores/ticketStore';
import { UserContext } from '../../context/UserContext';
import { useSSE } from '../../hooks/useSSE';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext)!;
  const { config, fetchConfig, toggleStatus } = useQueueStore();
  const { todayData, fetchTodayData } = useDashboardStore();
  const { fetchWaitingList, callNext } = useTicketStore();

  const refreshAll = useCallback(() => {
    fetchConfig();
    fetchTodayData();
    fetchWaitingList();
  }, []);

  // 初次載入
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // SSE 即時更新
  useSSE({
    storeId: user?.organizationId || '',
    enabled: !!user?.organizationId,
    onEvent: () => { refreshAll(); },
  });

  // 降級備援：30 秒輪詢
  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const handleCallNext = async () => {
    try {
      await callNext();
      fetchConfig();
      fetchTodayData();
    } catch (e) {}
  };

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('navigation.dashboard')}
        </h1>
      </div>

      {/* 即時狀態卡片 — text-5xl 保留：操作人員需要一眼看清號碼 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* 目前服務號碼 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('queue.dashboard.currentServing')}</p>
          <p className="text-5xl font-bold text-brand-600">
            #{config?.currentServing || 0}
          </p>
        </div>

        {/* 等候人數 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('queue.dashboard.waitingCount')}</p>
          <p className="text-5xl font-bold text-orange-500">
            {todayData?.waiting || 0}
          </p>
        </div>

        {/* 今日已服務 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('queue.dashboard.completedToday')}</p>
          <p className="text-5xl font-bold text-green-500">
            {todayData?.completed || 0}
          </p>
        </div>

        {/* 平均等待時間 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('queue.dashboard.avgWait')}</p>
          <p className="text-5xl font-bold text-blue-500">
            {todayData?.avgWaitMinutes || 0}<span className="text-lg">{t('queue.common.minutes')}</span>
          </p>
        </div>
      </div>

      {/* 快速操作 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* 叫下一號 */}
        <button
          onClick={handleCallNext}
          disabled={!config?.isActive || (todayData?.waiting || 0) === 0}
          className={`p-8 rounded-lg shadow text-center transition-all duration-200 ${
            config?.isActive && (todayData?.waiting || 0) > 0
              ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
              : 'bg-slate-300 dark:bg-slate-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
        >
          <p className="text-2xl font-bold">{t('queue.actions.callNext')}</p>
          <p className="text-sm mt-2 opacity-80">
            {(todayData?.waiting || 0) > 0
              ? t('queue.dashboard.nextInLine', { count: todayData?.waiting || 0 })
              : t('queue.dashboard.noOneWaiting')
            }
          </p>
        </button>

        {/* 排隊開關 */}
        <button
          onClick={() => toggleStatus(!config?.isActive)}
          className={`p-8 rounded-lg shadow text-center transition-all duration-200 ${
            config?.isActive
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          <p className="text-2xl font-bold">
            {config?.isActive ? t('queue.actions.pauseQueue') : t('queue.actions.startQueue')}
          </p>
          <p className="text-sm mt-2 opacity-80">
            {config?.isActive ? t('queue.dashboard.queueIsOpen') : t('queue.dashboard.queueIsClosed')}
          </p>
        </button>
      </div>

      {/* 今日統計摘要 */}
      {todayData && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.dashboard.todaySummary')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{todayData.totalIssued}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.dashboard.totalIssued')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-500">{todayData.completed}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.dashboard.completed')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{todayData.waiting}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.dashboard.waiting')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-500">{todayData.noShow}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.dashboard.noShow')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-500">{todayData.noShowRate}%</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.dashboard.noShowRate')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
